import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import {
  Search,
  Filter,
  Download,
  Eye,
  Trash2,
  ArrowUpCircle,
  ArrowDownCircle,
  Bell,
  X as XIcon,
  ListChecks,
  CheckCircle2,
  Sparkles,
  Clock,
  ChevronDown,
} from "lucide-react";
/* Simple generic modal component */

import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";

import NotificationStatusBadge from "./NotificationStatusBadge";
import { NotificationContext } from "../context/NotificationContext";
import Pagination, { usePagination } from '../shared/Pagination';
function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}



function Modal({ open, title, onClose, children, footer }) {
  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    // Prevent body scroll when modal is open
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <>
      {/* Backdrop rendered at body level — no parent clipping */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm"
        style={{ zIndex: 9998 }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="fixed inset-0 flex items-center justify-center px-4 py-6 pointer-events-none"
        style={{ zIndex: 9999 }}
      >
        <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[90vh] pointer-events-auto">

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center">
                <Bell size={15} className="text-teal-600" />
              </div>
              <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
            </div>
            <button
              onClick={onClose}
              aria-label="Close modal"
              className="w-7 h-7 rounded-xl border border-gray-200 flex items-center justify-center text-gray-400 hover:bg-gray-50 hover:text-gray-600 transition-colors"
            >
              <span className="text-base leading-none">&times;</span>
            </button>
          </div>

          {/* Body */}
          <div className="p-5 overflow-y-auto flex-1">{children}</div>

          {/* Footer */}
          {footer && (
            <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/60 flex justify-end gap-2">
              {footer}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body  // ← teleports outside your page layout entirely
  );
}

export default function NotificationsTable() {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  // const [page, setPage] = useState(1);
  // const pageSize = 8;

  // Use state so we can mutate (delete, mark read)
  // const [notifications, setNotifications] = useState([]);

  const { notifications, fetchNotifications, deleteNotification } = useContext(NotificationContext);

  const location = useLocation();
  const navigate = useNavigate();

  // Set when we arrive here from a notification click in the header dropdown.
  // Drives an exact-match filter instead of the free-text search.
  const [highlightId, setHighlightId] = useState(null);

  const autoOpenedRef = React.useRef(null);

  // Modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);

  // Selected notification for view/delete actions
  const [selectedNotification, setSelectedNotification] = useState(null);

  // Export option state
  const [exportFormat, setExportFormat] = useState("csv");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const id = location.state?.notificationId;
    if (id !== undefined && id !== null) {
      autoOpenedRef.current = null; // allow re-opening even if this id was seen before
      setHighlightId(id);
      setQuery(String(id)); // show the ID in the search box, not blank
      setStatusFilter("all");
      // Clear the nav state so a refresh / back-nav doesn't keep re-applying it
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  useEffect(() => {
    if (highlightId === null) return;
    if (autoOpenedRef.current === highlightId) return; // already handled this one
    const match = notifications.find((n) => String(n.id) === String(highlightId));
    if (match) {
      autoOpenedRef.current = highlightId;
      setSelectedNotification(match);
      setViewModalOpen(true);
      if (match.status !== "read") markAsRead(match.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightId, notifications]);

  function clearHighlight() {
    setHighlightId(null);
    autoOpenedRef.current = null;
  }

const filtered = useMemo(() => {
  // Exact-match filter takes priority when we've arrived from a notification click
  if (highlightId !== null) {
    return notifications.filter((n) => String(n.id) === String(highlightId));
  }

  const q = query.trim().toLowerCase();

  return notifications.filter((n) => {

    const status = (n.status || "").toLowerCase();

    /* STATUS FILTER */
    if (statusFilter === "unread") {
      if (status === "read") return false;
    } 
    else if (statusFilter !== "all") {
      if (status !== statusFilter) return false;
    }

    /* SEARCH */
   if (!q) return true;

const safe = (val) => String(val ?? "").toLowerCase();


  return (
  safe(n.id).includes(q) ||
  safe(n.title).includes(q) ||
  safe(n.message).includes(q) ||
  safe(n.customer).includes(q) ||
  safe(n.email).includes(q) ||
  safe(n.first_name).includes(q) ||
  safe(n.last_name).includes(q) ||
  safe(n.user_email).includes(q)
);
  });

}, [notifications, query, statusFilter, highlightId]);
  
const {
  page,
  pageSize,
  setPage,
  setPageSize,
  totalPages,
  totalItems,
  paginated: visible,
} = usePagination(filtered, 8);

useEffect(() => { setPage(1); }, [query, statusFilter, highlightId]);
  // const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  // const visible = filtered.slice((page - 1) * pageSize, page * pageSize);

  // Stats derived from state
  const stats = useMemo(() => ({
    total: notifications.length,
    unread: notifications.filter((t) => t.status !== "read").length,
    newCount: notifications.filter((t) => t.status === "new").length
  }), [notifications]);

  // Handlers
  function openView(n) {
    setSelectedNotification(n);
    setViewModalOpen(true);
    if (n.status !== "read") markAsRead(n.id);
  }

  function openDelete(n) {
    setSelectedNotification(n);
    setDeleteModalOpen(true);
  }

  async function confirmDelete() {
  if (!selectedNotification) return;
  await deleteNotification(selectedNotification.id); // context handles state update
  setDeleteModalOpen(false);
  setSelectedNotification(null);
}


  function markAsRead(id) {
  fetchNotifications(); // refetch after marking as read from API
}

  function handleExportOpen() {
    setExportFormat("csv");
    setDownloadModalOpen(true);
  }

  function downloadCSV(data, filename = "notifications.csv") {
    const header = ["id","title","message","customer","email","status","create_audit_time"];
    const rows = data.map((d) => header.map((h) => {
      const val = d[h] ?? "";
      return `"${String(val).replace(/"/g, '""')}"`;
    }).join(","));
    const csv = [header.join(","), ...rows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function downloadJSON(data, filename = "notifications.json") {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function confirmExport() {
    setExporting(true);
    try {
      const dataToExport = filtered;
      await new Promise((r) => setTimeout(r, 350));
      if (exportFormat === "csv") downloadCSV(dataToExport);
      else downloadJSON(dataToExport);
      setDownloadModalOpen(false);
    } catch (err) {
      console.error("Export failed", err);
    } finally {
      setExporting(false);
    }
  }

  function handleMarkRead(n) {
    if (n) markAsRead(n.id);
  }

  // useEffect(() => setPage(1), [query, statusFilter]);

  // Get most recent create_audit_time for "Last update" card
  const lastUpdate = useMemo(() => {
    if (!notifications.length) return null;
    return notifications.slice().sort((a, b) => new Date(b.create_audit_time) - new Date(a.create_audit_time))[0].create_audit_time;
  }, [notifications]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Notifications', value: stats.total,    icon: ListChecks,   iconBg: 'bg-teal-50',   iconColor: 'text-teal-600',   valueColor: 'text-gray-800' },
          { label: 'Unread',              value: stats.unread,   icon: CheckCircle2, iconBg: 'bg-amber-50',  iconColor: 'text-amber-500',  valueColor: 'text-amber-600' },
          // { label: 'New',                 value: stats.newCount, icon: Sparkles,     iconBg: 'bg-green-50',  iconColor: 'text-green-600',  valueColor: 'text-green-600' },
          { label: 'Last Update',         value: lastUpdate ? formatDate(lastUpdate) : '—', icon: Clock, iconBg: 'bg-gray-100', iconColor: 'text-gray-400', valueColor: 'text-gray-500', small: true },
        ].map(({ label, value, icon: Icon, iconBg, iconColor, valueColor, small }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3 shadow-sm">
            <div className={`w-11 h-11 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
              <Icon className={`w-5 h-5 ${iconColor}`} />
            </div>
            <div className="min-w-0">
              <p className={`${small ? "text-sm" : "text-lg"} font-bold ${valueColor} leading-tight truncate`}>{value}</p>
              <p className="text-xs text-gray-400 mt-0.5">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Active "filtered to one notification" banner */}
      {highlightId !== null && (
        <div className="flex items-center justify-between gap-3 bg-teal-50 border border-teal-100 rounded-xl px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-teal-700">
            <Bell size={15} />
            <span>Showing notification <strong>#{highlightId}</strong> only.</span>
          </div>
          <button
            onClick={clearHighlight}
            className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 hover:text-teal-900 px-2 py-1 rounded-lg hover:bg-teal-100 transition-colors"
          >
            <XIcon size={14} /> Clear filter
          </button>
        </div>
      )}

      {/* search + filters */}
      <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
        <div className="flex flex-col md:flex-row gap-3 items-center">
          <div className="flex-1 w-full">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
              <input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setPage(1); if (highlightId !== null) clearHighlight(); }}
                placeholder="Search by ID, title, customer, or email..."
                className="w-full pl-11 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-400 transition-colors"
              />
            </div>
          </div>

          <div className="flex gap-2 items-center w-full md:w-auto">
            <div className="relative w-full md:w-auto">
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                className="appearance-none pl-4 pr-9 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-600 bg-white w-full md:w-auto focus:outline-none focus:ring-2 focus:ring-teal-100 focus:border-teal-400 transition-colors"
              >
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="unread">Unread</option>
                <option value="read">Read</option>
                <option value="archived">Archived</option>
              </select>
              <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>

            <button
              onClick={handleExportOpen}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors flex-shrink-0"
            >
              <Download size={16} /> Export
            </button>
          </div>
        </div>
      </div>

      {/* Showing X of Y */}
      <p className="text-sm text-gray-500 px-1">
        Showing <span className="font-semibold text-gray-700">{visible.length}</span> of{" "}
        <span className="font-semibold text-gray-700">{totalItems}</span> notifications
      </p>

      {/* TABLE for md+ screens */}
      <div className="hidden md:block bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm">
        <table className="min-w-full divide-y">
          <thead className="bg-teal-700 text-white">
            <tr>
              <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-white">Notification</th>
              <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-white">Sender / Target</th>
              <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-white">Status</th>
              <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-white">Date</th>
              <th className="px-6 py-3.5 text-right text-xs font-semibold uppercase tracking-wide text-white">Actions</th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y">
            {visible.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-stone-400">No notifications found.</td>
              </tr>
            )}

            {visible.map((n) => (
              <tr key={n.id} className="hover:bg-stone-50">
                <td className="px-6 py-4 align-top">
                  <div className="text-sm font-semibold text-stone-700">{n.title}</div>
                  <div className="text-xs text-stone-500 mt-1">{n.message}</div>
                  
                </td>

                <td className="px-6 py-4 align-top">
                  {n.first_name ? (
                    <div>
                      <div className="text-sm font-medium">{n.first_name} {n.last_name}</div>
                      <div className="text-xs text-stone-500">{n.user_email}</div>
                    </div>
                  ) : (
                    <div className="text-sm text-stone-500">System</div>
                  )}
                </td>

                <td className="px-6 py-4 align-top">
                  <NotificationStatusBadge status={n.status} />
                </td>

                <td className="px-6 py-4 align-top text-sm text-stone-600">{formatDate(n.create_audit_time)}</td>

                <td className="px-6 py-4 text-right align-top">
                  <div className="flex items-center justify-end gap-3">
                    <button
                      title="View"
                      onClick={() => openView(n)}
                      className="p-2 rounded-full hover:bg-stone-100"
                      aria-label={`View ${n.title}`}
                    >
                      <Eye size={16} />
                    </button>
                    {/* <button
                      title="Mark read"
                      onClick={() => handleMarkRead(n)}
                      className="p-2 rounded-full hover:bg-stone-100"
                      aria-label={`Mark ${n.title} as read`}
                    >
                      <ArrowDownCircle size={16} />
                    </button> */}
                    <button
                      title="Delete"
                      onClick={() => openDelete(n)}
                      className="p-2 rounded-full hover:bg-stone-100"
                      aria-label={`Delete ${n.title}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* CARD list for small screens */}
      <div className="md:hidden space-y-3">
        {visible.length === 0 && (
          <div className="p-4 bg-white border rounded text-center text-stone-400">No notifications found.</div>
        )}

        {visible.map((n) => (
          <div key={n.id} className="bg-white border rounded-lg p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-stone-700">{n.title}</div>
                  <div className="text-xs text-stone-500">{formatDate(n.create_audit_time)}</div>
                </div>

                <div className="text-xs text-stone-500 mt-2">{n.message}</div>
                <div className="text-xs text-stone-400 mt-2">ID: {n.id}</div>

                <div className="mt-3 flex items-center gap-3">
                  <div className="min-w-[92px]">
                    {n.customer ? (
                      <div className="text-xs text-stone-600">{n.customer}</div>
                    ) : (
                      <div className="text-xs text-stone-600">System</div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <NotificationStatusBadge status={n.status} />
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-end gap-2">
                <button onClick={() => openView(n)} className="p-2 rounded-md hover:bg-stone-100" aria-label={`View ${n.title}`}>
                  <Eye size={18} />
                </button>
                {/* <button onClick={() => handleMarkRead(n)} className="p-2 rounded-md hover:bg-stone-100" aria-label={`Mark ${n.title} as read`}>
                  <ArrowDownCircle size={18} />
                </button> */}
                <button onClick={() => openDelete(n)} className="p-2 rounded-md hover:bg-stone-100" aria-label={`Delete ${n.title}`}>
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* pagination */}
    {totalItems > 0 && (
  <div className="border-t border-gray-100 bg-white rounded-xl mt-4">
    <Pagination
      currentPage={page}
      totalPages={totalPages}
      totalItems={totalItems}
      pageSize={pageSize}
      onPageChange={setPage}
      onPageSizeChange={setPageSize}
    />
  </div>
)}

      {/* Modals (unchanged behavior) */}
      <Modal
        open={viewModalOpen}
        title="Notification Details"
        onClose={() => {
          setViewModalOpen(false);
          setSelectedNotification(null);
        }}
        footer={
          <div className="flex items-center gap-2">
            {/* <button
              onClick={() => {
                if (selectedNotification) markAsRead(selectedNotification.id);
              }}
              className="px-4 py-2 bg-emerald-600 text-white rounded"
            >
              Mark as read
            </button> */}
            <button
              onClick={() => {
                setViewModalOpen(false);
                setSelectedNotification(null);
              }}
              className="px-4 py-2 border rounded bg-teal-600"
            >
              Close
            </button>
          </div>
        }
      >
       {selectedNotification ? (
  <div className="space-y-5">
    {/* Title + status */}
    <div className="flex items-start justify-between gap-3">
      <h4 className="text-sm font-semibold text-gray-900 leading-snug">
        {selectedNotification.title}
      </h4>
      <NotificationStatusBadge status={selectedNotification.status} />
    </div>

    {/* Message */}
    <div className="bg-gray-50 rounded-lg px-4 py-3 text-sm text-gray-600 leading-relaxed">
      {selectedNotification.message}
    </div>

    {/* Meta grid */}
    <div className="grid grid-cols-2 gap-4">
      <div>
        <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 mb-1">Date</p>
        <p className="text-sm text-gray-700">{formatDate(selectedNotification.create_audit_time)}</p>
      </div>
      <div>
        <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 mb-1">Status</p>
        <p className="text-sm text-gray-700 capitalize">{selectedNotification.status}</p>
      </div>
    </div>

    <hr className="border-gray-100" />

    {/* Sender */}
    <div>
      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 mb-2">Sender / Target</p>
      {selectedNotification.first_name ? (
        <div className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3">
          <div className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-semibold text-xs flex-shrink-0">
            {selectedNotification.first_name[0]}{selectedNotification.last_name?.[0] ?? ""}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-800">
              {selectedNotification.first_name} {selectedNotification.last_name}
            </p>
            <p className="text-xs text-gray-500">{selectedNotification.user_email}</p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3">
          <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
            <Bell size={14} className="text-gray-500" />
          </div>
          <p className="text-sm text-gray-500">System notification</p>
        </div>
      )}
    </div>

    {/* ID */}
    <p className="text-[11px] text-gray-300 font-mono">ID: {selectedNotification.id}</p>
  </div>
) : (
  <div className="text-center text-gray-400 py-8">No notification selected.</div>
)}
      </Modal>

      <Modal
        open={deleteModalOpen}
        title="Delete notification"
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedNotification(null);
        }}
        footer={
          <div className="flex items-center gap-2">
            <button
              onClick={confirmDelete}
              className="px-4 py-2 bg-red-600 text-white rounded"
            >
              Delete
            </button>
            <button
              onClick={() => {
                setDeleteModalOpen(false);
                setSelectedNotification(null);
              }}
              className="px-4 py-2 border rounded"
            >
              Cancel
            </button>
          </div>
        }
      >
        <div>
          <p className="text-sm text-stone-600 mb-4">Are you sure you want to delete <strong>{selectedNotification?.title ?? "this notification"}</strong>? This action cannot be undone.</p>
        </div>
      </Modal>

      <Modal
        open={downloadModalOpen}
        title="Export Notifications"
        onClose={() => setDownloadModalOpen(false)}
        footer={
          <div className="flex items-center gap-2">
            <button
              onClick={confirmExport}
              disabled={exporting}
              className="px-4 py-2 bg-emerald-600 text-white rounded disabled:opacity-50"
            >
              {exporting ? "Exporting..." : `Export (${exportFormat.toUpperCase()})`}
            </button>
            <button onClick={() => setDownloadModalOpen(false)} className="px-4 py-2 border rounded">Cancel</button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-stone-600">Export <strong>filtered</strong> notifications (based on your current search & filters).</p>

          <div className="flex items-center gap-4">
            <label className={`px-4 py-2 border rounded cursor-pointer ${exportFormat === "csv" ? "bg-stone-100" : ""}`}>
              <input
                type="radio"
                name="exportFormat"
                value="csv"
                checked={exportFormat === "csv"}
                onChange={() => setExportFormat("csv")}
                className="mr-2"
              />
              CSV
            </label>

            <label className={`px-4 py-2 border rounded cursor-pointer ${exportFormat === "json" ? "bg-stone-100" : ""}`}>
              <input
                type="radio"
                name="exportFormat"
                value="json"
                checked={exportFormat === "json"}
                onChange={() => setExportFormat("json")}
                className="mr-2"
              />
              JSON
            </label>
          </div>

          <div className="text-xs text-stone-400">Note: The exported file will contain only the notifications currently shown by your filters.</div>
        </div>
      </Modal>
      </div>
    </div>
  );
}